import { Avatar } from "@mui/material";
import { artworkNatureList, type ArtworkNatureOption } from "artworkNatureList";
import Plus from "assets/img/svg/plus";
import ModalBox from "components/Modal";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import StyledButton from "components/shared/StyledButton";
import NewAnalyticalCode from "components/TitleFormContainer/NewAnalyticalCode";
import NewGroupModal from "containers/Channels/NewGroupModal";
import type { IWork } from "containers/Works/types";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { fetchBrands, type IBrand } from "store/brandStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchAnalyticalCodes, fetchTitleGroups, type TitleGroup } from "store/titleStore";
import { createWork, resetCreateWorkStatus, resetUpdateWorkStatus, updateWork } from "store/workStore";

type NewWorkModalProps = {
    open: boolean;
    onClose: () => void;
    onCreated?: (work: IWork) => void;
    work?: IWork | null;
    initialName?: string;
    onRequestDelete?: () => void;
};

type WorkFormValues = {
    name: string;
    workCode: string;
    artworkNature: ArtworkNatureOption | null;
    analysisCode: string;
    associatedBrand: IBrand | null;
    compensationGroup: TitleGroup | null;
};

const artworkNatureFromWork = (work?: IWork | null) =>
    artworkNatureList.find((option) => option.code === work?.artworkNature) ?? artworkNatureList[0];

const defaultBrandFor = (currentWork: IWork | null | undefined, brands: IBrand[]): IBrand | null => {
    const brandName = currentWork?.associatedBrand?.trim() || "Par défaut";
    return brands.find((brand) => brand.name === brandName) ?? { name: brandName, avatarUrl: "" };
};

const defaultValuesFor = (
    currentWork: IWork | null | undefined,
    namePrefill: string,
    groups: TitleGroup[],
    brands: IBrand[],
): WorkFormValues => ({
    name: currentWork?.name ?? namePrefill,
    workCode: currentWork?.workCode ?? "",
    artworkNature: artworkNatureFromWork(currentWork),
    analysisCode: currentWork?.analysisCode ?? "",
    associatedBrand: defaultBrandFor(currentWork, brands),
    compensationGroup: groups.find((group) => group.id === currentWork?.compensationGroupId) ?? null,
});

const NewWorkModal = ({ open, onClose, onCreated, work, initialName = "", onRequestDelete }: NewWorkModalProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { createWorkStatus, updateWorkStatus } = useAppSelector((state) => state.works);
    const { titleGroups, titleGroupsStatus, codes } = useAppSelector((state) => state.titles);
    const { brands } = useAppSelector((state) => state.brands);
    const { countryFeatures, companyInfos, getCompanySettings } = useCompanySettings();
    const showArtworkField = countryFeatures?.urssaf === true;
    const usesTitleGroups = getCompanySettings("uses_intertitle_compensation") === true;
    const usesMultipleBrands = getCompanySettings("uses_multiple_brands") === true;
    const isEdit = work != null;
    const isPending = isEdit ? updateWorkStatus === "pending" : createWorkStatus === "pending";
    const [showCompensationGroupModal, setShowCompensationGroupModal] = useState(false);
    const [newTitleGroupId, setNewTitleGroupId] = useState<number | null>(null);
    const [openNewCodeModal, setOpenNewCodeModal] = useState(false);
    const [newCode, setNewCode] = useState("");
    const codeNames = useMemo(() => codes.map((code) => code.name), [codes]);

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isDirty },
    } = useForm<WorkFormValues>({
        defaultValues: defaultValuesFor(work, initialName, titleGroups, brands),
        mode: "onSubmit",
    });

    const currentName = watch("name")?.trim() ?? "";
    const currentArtwork = watch("artworkNature");
    const currentGroup = watch("compensationGroup");
    const currentCode = watch("analysisCode");
    const canSubmit = currentName.length > 0 && (!showArtworkField || currentArtwork != null) && (!isEdit || isDirty);

    useEffect(() => {
        if (!open) return;
        dispatch(fetchAnalyticalCodes());
        if (usesMultipleBrands) dispatch(fetchBrands(true));
    }, [dispatch, open, usesMultipleBrands]);

    useEffect(() => {
        if (open && usesTitleGroups && titleGroups.length === 0) {
            dispatch(fetchTitleGroups());
        }
    }, [dispatch, open, titleGroups.length, usesTitleGroups]);

    useEffect(() => {
        if (open) reset(defaultValuesFor(work, isEdit ? "" : initialName.trim(), titleGroups, brands));
    }, [open, reset, work, titleGroups, brands, initialName, isEdit]);

    useEffect(() => {
        if (currentCode && typeof currentCode === "object" && (currentCode as { id?: number }).id === -1) {
            setOpenNewCodeModal(true);
            setValue("analysisCode", "");
        }
    }, [currentCode, setValue]);

    useEffect(() => {
        if (!newCode) return;
        const code = codeNames.find((name) => name === newCode);
        if (code) setValue("analysisCode", code, { shouldDirty: true });
    }, [codeNames, newCode, setValue]);

    useEffect(() => {
        if (newTitleGroupId == null) return;
        setValue("compensationGroup", titleGroups.find((group) => group.id === newTitleGroupId) ?? null, {
            shouldDirty: true,
        });
    }, [newTitleGroupId, setValue, titleGroups]);

    useEffect(() => {
        if (currentGroup?.id !== -1) return;
        setShowCompensationGroupModal(true);
        setValue("compensationGroup", null, { shouldDirty: false });
    }, [currentGroup, setValue]);

    const handleClose = () => {
        reset(defaultValuesFor(null, "", titleGroups, brands));
        dispatch(resetCreateWorkStatus());
        dispatch(resetUpdateWorkStatus());
        onClose();
    };

    const payloadFromForm = (form: WorkFormValues) => ({
        name: form.name.trim(),
        workCode: form.workCode.trim() || null,
        artworkNature: showArtworkField ? form.artworkNature?.code : undefined,
        analysisCode: typeof form.analysisCode === "string" ? form.analysisCode.trim() || null : null,
        associatedBrand: usesMultipleBrands ? form.associatedBrand?.name ?? "Par défaut" : undefined,
        compensationGroupId: usesTitleGroups ? form.compensationGroup?.id ?? null : undefined,
    });

    const onSubmit: SubmitHandler<WorkFormValues> = async (form) => {
        const trimmedName = form.name.trim();
        if (!trimmedName) return;

        if (isEdit) {
            if (!isDirty) return;
            const result = await dispatch(updateWork({ id: work.id, payload: payloadFromForm(form) }));
            if (updateWork.fulfilled.match(result)) handleClose();
            return;
        }

        const result = await dispatch(createWork(payloadFromForm(form)));
        if (createWork.fulfilled.match(result)) {
            reset(defaultValuesFor(null, "", titleGroups, brands));
            onCreated?.(result.payload);
        }
    };

    const renderArtworkNatureOption = (props: object, option: { code: string; description: string }) => (
        <li {...props} className="artwork-nature-list">
            <span>{option.code}</span>
            <div className="separator" />
            <span>{option.description}</span>
        </li>
    );

    const renderAnalysisCodeOption = (props: object, option: { addElement?: string; id?: number } | string) =>
        typeof option === "object" && option.addElement ? (
            <li {...props} key={option.id} className="autocomplete-new-element">
                <div className="content">
                    <Plus />
                    {option.addElement}
                </div>
            </li>
        ) : (
            <li {...props}>
                <span>{typeof option === "string" ? option : ""}</span>
            </li>
        );

    const renderBrandsOption = (props: object, option: IBrand) => (
        <li {...props}>
            <Avatar
                variant="rounded"
                src={option.avatarUrl}
                sx={{
                    width: "24px",
                    height: "24px",
                    backgroundColor: "#FFF2F1",
                    color: "black",
                    fontSize: "12px",
                }}
            >
                {option.name?.slice(0, 1).toUpperCase()}
            </Avatar>
            <div className="separator" />
            <span>{companyInfos?.name && option.name === "Par défaut" ? companyInfos.name : option.name}</span>
        </li>
    );

    const renderTitleGroupsOption = (props: object, option: TitleGroup & { addElement?: string }) =>
        option.id !== -1 ? (
            <li {...props} key={option.id}>
                <span>{option.name}</span>
            </li>
        ) : (
            <li {...props} key={option.id} className="autocomplete-new-element">
                <div className="content">{option.addElement}</div>
            </li>
        );

    return (
        <>
            <ModalBox
                open={openNewCodeModal}
                onClose={() => setOpenNewCodeModal(false)}
                mainTitle={t("AnalyticalCode.AddAnalyticalCode")}
            >
                <NewAnalyticalCode
                    companyId={work?.companyId}
                    onClose={() => setOpenNewCodeModal(false)}
                    onCreate={(name: string) => {
                        setNewCode(name);
                    }}
                />
            </ModalBox>
            {showCompensationGroupModal && (
                <NewGroupModal
                    type="title"
                    openModal={showCompensationGroupModal}
                    onClose={() => setShowCompensationGroupModal(false)}
                    setNewGroupId={(id: number) => setNewTitleGroupId(id)}
                />
            )}
            <ModalBox
                open={open}
                onClose={handleClose}
                mainTitle={isEdit ? t("Works.EditWork") : t("Works.NewWork")}
                showCloseButton
            >
                <form
                    className="new-work-modal"
                    onSubmit={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        void handleSubmit(onSubmit)(event);
                    }}
                >
                    {!isEdit && <p className="new-work-modal__hint">{t("Works.CreateWorkHint")}</p>}
                    <TextFormComponent
                        label={t("Works.WorkName")}
                        placeholder={t("Works.WorkNamePlaceholder")}
                        control={control}
                        controlName="name"
                        required
                        fullWidth
                        error={errors.name}
                    />
                    <TextFormComponent
                        label={t("Works.WorkCode")}
                        placeholder={t("Works.WorkCodePlaceholder")}
                        control={control}
                        controlName="workCode"
                        fullWidth
                        error={errors.workCode}
                    />
                    {showArtworkField && (
                        <AutocompleteFormComponent
                            label={t("NewPublication.ArtworkNature")}
                            control={control}
                            controlName="artworkNature"
                            options={artworkNatureList}
                            renderOption={renderArtworkNatureOption}
                            renderOptionText={(option: ArtworkNatureOption) => `${option.code} | ${option.description}`}
                            defaultFilterOptions
                            error={errors.artworkNature}
                            required
                        />
                    )}
                    <AutocompleteFormComponent
                        label={t("ImportData.TitleAnalysisCode")}
                        control={control}
                        controlName="analysisCode"
                        options={codeNames}
                        renderOptionText={(option: string) => option}
                        addElement={t("AnalyticalCode.AddAnalyticalCode")}
                        renderOption={renderAnalysisCodeOption}
                        defaultFilterOptions
                        error={errors.analysisCode}
                    />
                    {usesMultipleBrands && (
                        <AutocompleteFormComponent
                            label={t("NewPublication.Brand")}
                            control={control}
                            controlName="associatedBrand"
                            options={brands}
                            renderOptionText={(option: IBrand) =>
                                companyInfos?.name && option.name === "Par défaut" ? companyInfos.name : option.name
                            }
                            renderOption={renderBrandsOption}
                            isOptionEqualToValue={(option, value) => option.name === value.name}
                            error={errors.associatedBrand}
                            defaultFilterOptions
                        />
                    )}
                    {usesTitleGroups && (
                        <AutocompleteFormComponent
                            options={titleGroups ?? []}
                            renderOptionText={(option) => option?.name ?? option}
                            renderOption={renderTitleGroupsOption}
                            control={control}
                            controlName="compensationGroup"
                            placeholder={t("NewTitle.GroupNoSelection")}
                            label={t("NewChannel.CompensationGroup")}
                            defaultFilterOptions
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            addElement={t("NewChannel.AddCompensationGroup")}
                            loading={titleGroupsStatus === "pending" || titleGroupsStatus === "idle"}
                        />
                    )}
                    <div className="new-work-modal__actions">
                        {isEdit && onRequestDelete ? (
                            <StyledButton buttonType="danger" fullWidth onClick={onRequestDelete}>
                                {t("Works.DeleteWork")}
                            </StyledButton>
                        ) : (
                            <StyledButton buttonType="outlined" fullWidth onClick={handleClose}>
                                {t("Works.Cancel")}
                            </StyledButton>
                        )}
                        <StyledButton
                            type="submit"
                            buttonType="primary"
                            fullWidth
                            loading={isPending}
                            disabled={!canSubmit}
                        >
                            {isEdit ? t("Works.Validate") : t("Works.CreateWork")}
                        </StyledButton>
                    </div>
                </form>
            </ModalBox>
        </>
    );
};

export default NewWorkModal;
